import React, { createContext, useContext, useEffect, useRef, useMemo } from 'react';
import { Platform } from 'react-native';
import { useIAP } from '@utils/iapUtils';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { SHOW_AUTH_MODAL, HIDE_LOADING_MODAL, SHOW_LOADING_MODAL } from '@reduxLocal/actions/types';
import apis from '@api/api';
import DeviceInfo from 'react-native-device-info';
import { isDevice } from 'expo-device';

interface IAPContextType {
  connected: boolean;
  products: any[];
  fetchProducts: (params: { skus: string[]; type?: any }) => Promise<void>;
  requestPurchase: (params: any) => Promise<any>;
  finishTransaction: (params: any) => Promise<void>;
  getAvailablePurchases: () => Promise<any[]>;
  productIds: string[];
  isSimulator: boolean | null;
}

export const IAPContext = createContext<IAPContextType | null>(null);

export const useIAPContext = () => {
  const context = useContext(IAPContext);
  if (!context) {
    throw new Error('useIAPContext must be used within IAPProvider');
  }
  return context;
};

export const IAPProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const hasFetchedRef = useRef(false);
  const isFetchingRef = useRef(false);
  const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousConnectionStateRef = useRef<boolean | null>(null);
  const [isSimulator, setIsSimulator] = React.useState<boolean | null>(null);

  // Product IDs for subscriptions
  const productIds = useMemo(() => Platform.OS === 'android'
    ? [
      'prod_rcyg8ax9fok7ki',
      'prod_rsjvyro6u0sunk',
      'prod_RCyg8ax9foK7ki',
    ]
    : [
      'prod_RCyg8ax9foK7ki',
      'com.anonymous.smartsaas.premium',
      'prod_RSjvYro6u0sunK',
      'prod_NRIXP6Afr9Ldzj',
    ], []);

  // Check if running on simulator/emulator
  useEffect(() => {
    const checkSimulator = async () => {
      try {
        if (Platform.OS === 'ios') {
          const isEmulator = !isDevice;
          setIsSimulator(isEmulator);
          if (isEmulator) {
            console.log('📱 [IAP Context] Running on iOS Simulator - IAP fetching enabled. Use Local StoreKit Testing in Xcode for full IAP testing.');
          }
        } else if (Platform.OS === 'android') {
          const isEmulator = await DeviceInfo.isEmulator();
          setIsSimulator(isEmulator);
          if (isEmulator) {
            console.log('📱 [IAP Context] Running on Android Emulator - IAP fetching enabled. For best results, test on a real device.');
          }
        }
      } catch (error) {
        console.error('[IAP Context] Error checking simulator status:', error);
        setIsSimulator(false);
      }
    };
    checkSimulator();
  }, []);

  // useIAP hook automatically manages connection lifecycle
  // See: https://hyochan.github.io/expo-iap/guides/lifecycle
  console.log('🔵 [IAP Context] useIAP Hook Initializing at App Level:', {
    platform: Platform.OS,
    timestamp: new Date().toISOString(),
    note: 'Connection will be automatically established by useIAP hook'
  });

  const {
    connected,
    products,
    fetchProducts,
    requestPurchase,
    finishTransaction,
    getAvailablePurchases,
  } = useIAP({
    onPurchaseSuccess: async (purchase) => {
      try {
        console.log('🛒 [IAP Context] Purchase Success Event:', {
          purchaseId: purchase.id,
          productId: (purchase as any).productId || purchase.id,
          transactionId: (purchase as any).transactionId || purchase.id,
          platform: Platform.OS,
          timestamp: new Date().toISOString(),
        });

        dispatch({ type: SHOW_LOADING_MODAL, content: "Processing purchase..." });

        // IMPORTANT: Verify receipt on your backend before finishing transaction
        console.log('🔐 [IAP Context] Starting receipt validation...');
        const isValid = await apis.updateBilling(purchase).then((res) => {
          console.log('✅ [IAP Context] Receipt validation succeeded:', {
            message: res.data?.message,
            timestamp: new Date().toISOString()
          });

          dispatch({ type: HIDE_LOADING_MODAL });
          if (res.data.message) {
            setTimeout(() => {
              dispatch({
                type: SHOW_AUTH_MODAL,
                data: { content: res.data.message, type: 'success' }
              });
            }, 1000);
          }
          return true;
        }).catch((err) => {
          console.error('❌ [IAP Context] Receipt validation failed:', {
            error: err,
            message: err?.message,
            stack: err?.stack,
            timestamp: new Date().toISOString()
          });

          dispatch({ type: HIDE_LOADING_MODAL });
          dispatch({
            type: SHOW_AUTH_MODAL,
            data: { content: "Purchase verification failed", type: 'error' }
          });
          return false;
        });

        if (!isValid) {
          console.error('❌ [IAP Context] Purchase validation failed - not finishing transaction');
          return;
        }

        // Finish the transaction
        console.log('✅ [IAP Context] Finishing transaction...');
        await finishTransaction({
          purchase,
          isConsumable: false, // Subscriptions are not consumable
        });
        console.log('✅ [IAP Context] Transaction finished successfully');

        // Refetch subscription data
        console.log('🔄 [IAP Context] Refetching subscription data...');
        queryClient.refetchQueries({ queryKey: ['subscriptionPlan'] });
        queryClient.refetchQueries({ queryKey: ['billingInfo'] });
      } catch (error) {
        console.error('❌ [IAP Context] Failed to complete purchase:', {
          error,
          message: error?.message,
          stack: error?.stack,
          timestamp: new Date().toISOString()
        });
        dispatch({ type: HIDE_LOADING_MODAL });
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: { content: "Failed to complete purchase", type: 'error' }
        });
      }
    },
    onPurchaseError: (error) => {
      console.error('❌ [IAP Context] Purchase Error Event:', {
        error,
        code: error?.code,
        message: error?.message,
        name: error?.name,
        stack: error?.stack,
        platform: Platform.OS,
        timestamp: new Date().toISOString()
      });

      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: "Your purchase was not completed", type: 'warning' }
      });
    },
  });

  // Connection lifecycle logging
  useEffect(() => {
    const connectionState = connected ? 'CONNECTED' : 'DISCONNECTED';
    const previousState = previousConnectionStateRef.current === null
      ? 'UNKNOWN'
      : previousConnectionStateRef.current
        ? 'CONNECTED'
        : 'DISCONNECTED';

    if (previousConnectionStateRef.current !== connected) {
      if (connected) {
        console.log('✅ [IAP Context] Store Connection Established:', {
          state: 'CONNECTED',
          platform: Platform.OS,
          readyForOperations: true,
          availableMethods: ['fetchProducts', 'requestPurchase', 'getAvailablePurchases'],
          timestamp: new Date().toISOString(),
          note: 'You can now safely call store methods like fetchProducts()'
        });
      } else {
        console.log('⏳ [IAP Context] Store Disconnected:', {
          state: 'DISCONNECTED',
          platform: Platform.OS,
          timestamp: new Date().toISOString(),
          note: 'Waiting for automatic reconnection...'
        });
      }

      console.log(`🔄 [IAP Context] Connection State Transition:`, {
        from: previousState,
        to: connectionState,
        platform: Platform.OS,
        timestamp: new Date().toISOString()
      });

      previousConnectionStateRef.current = connected;
    } else if (connected && previousConnectionStateRef.current === null) {
      console.log('✅ [IAP Context] Initial Store Connection:', {
        state: 'CONNECTED',
        platform: Platform.OS,
        readyForOperations: true,
        timestamp: new Date().toISOString(),
        note: 'Initial connection established by useIAP hook'
      });
      previousConnectionStateRef.current = connected;
    }
  }, [connected]);

  // Fetch products when connected (only once, with debounce)
  useEffect(() => {
    // Clear any pending timeout
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }

    if (isSimulator === true && Platform.OS === 'ios') {
      console.log('📱 [IAP Context] Running on iOS Simulator - IAP fetching enabled (use Local StoreKit Testing in Xcode)');
    } else if (isSimulator === true && Platform.OS === 'android') {
      console.log('📱 [IAP Context] Running on Android Emulator - IAP fetching enabled (may have limited support)');
    }

    if (connected && !hasFetchedRef.current && !isFetchingRef.current) {
      console.log('⏰ [IAP Context] Connection ready - scheduling product fetch (500ms debounce)...');

      connectionTimeoutRef.current = setTimeout(() => {
        if (connected && !hasFetchedRef.current && !isFetchingRef.current) {
          hasFetchedRef.current = true;
          isFetchingRef.current = true;

          console.log('🛒 [IAP Context] Starting Product Fetch:', {
            productIds,
            platform: Platform.OS,
            productIdsCount: productIds.length,
            productIdsList: productIds,
            isSimulator: isSimulator === true ? 'YES - Limited support' : 'NO - Full support',
            timestamp: new Date().toISOString()
          });

          try {
            console.log('📞 [IAP Context] Calling fetchProducts API:', {
              skus: productIds,
              type: 'subs',
              skusCount: productIds.length,
              timestamp: new Date().toISOString()
            });

            const fetchStartTime = Date.now();
            fetchProducts({ skus: productIds, type: 'subs' })
              .then(() => {
                const fetchDuration = Date.now() - fetchStartTime;
                console.log('✅ [IAP Context] fetchProducts promise resolved:', {
                  duration: `${fetchDuration}ms`,
                  timestamp: new Date().toISOString(),
                  note: 'Products update asynchronously via hook - checking for updates...'
                });

                // Products update asynchronously via the useIAP hook
                // Use a ref to track the latest products value to avoid stale closures
                const checkProducts = (attempt: number, delay: number) => {
                  setTimeout(() => {
                    // Access products from the hook's current state
                    // This will be checked via the products state change effect instead
                    // to avoid stale closure issues
                    console.log(`📦 [IAP Context] Product Check Attempt ${attempt}:`, {
                      attempt,
                      delay,
                      elapsedSinceFetch: `${Date.now() - fetchStartTime}ms`,
                      timestamp: new Date().toISOString(),
                      note: 'Products will be checked via state change effect'
                    });

                    // Don't check products here - let the products state change effect handle it
                    // This avoids stale closure issues and infinite loops
                    if (attempt >= 5) {
                      isFetchingRef.current = false;
                      console.log('⏳ [IAP Context] Product check timeout reached - products will update via state change');
                    } else {
                      checkProducts(attempt + 1, delay * 2);
                    }
                  }, delay);
                };

                // Only start checking if we're still connected
                if (connected) {
                  checkProducts(1, 500);
                } else {
                  isFetchingRef.current = false;
                }

                checkProducts(1, 500);
              })
              .catch((error) => {
                const fetchDuration = Date.now() - fetchStartTime;
                console.error('❌ [IAP Context] Product Fetch Error:', {
                  error,
                  duration: `${fetchDuration}ms`,
                  errorDetails: {
                    message: error?.message,
                    code: error?.code,
                    name: error?.name,
                    stack: error?.stack
                  },
                  productIdsAttempted: productIds,
                  platform: Platform.OS,
                  timestamp: new Date().toISOString()
                });

                hasFetchedRef.current = false; // Allow retry on error
                isFetchingRef.current = false;
              });
          } catch (error) {
            console.error('❌ [IAP Context] Exception calling fetchProducts:', {
              error,
              message: error?.message,
              stack: error?.stack,
              timestamp: new Date().toISOString()
            });
            hasFetchedRef.current = false;
            isFetchingRef.current = false;
          }
        }
      }, 500); // 500ms debounce
    }

    // Cleanup timeout on unmount
    return () => {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
    };
  }, [connected, fetchProducts, productIds, isSimulator]);

  // Log products when they change
  const previousProductsLengthRef = useRef<number | null>(null);
  useEffect(() => {
    const currentProductsLength = products?.length || 0;
    const previousProductsLength = previousProductsLengthRef.current ?? null;

    if (currentProductsLength !== previousProductsLength) {
      console.log('🔄 [IAP Context] Products State Change:', {
        previousCount: previousProductsLength,
        currentCount: currentProductsLength,
        hasProducts: !!products,
        connected,
        productIds: productIds.length,
        platform: Platform.OS,
        timestamp: new Date().toISOString()
      });

      if (products && products.length > 0) {
        console.log('✅ [IAP Context] Products Available:', {
          count: products.length,
          products: products.map(p => ({
            id: p.id,
            title: p.title,
            price: p.displayPrice || p.price,
            currency: p.currency,
          })),
          timestamp: new Date().toISOString()
        });
        // Mark fetching as complete when products are available
        isFetchingRef.current = false;
      }
    }

    previousProductsLengthRef.current = currentProductsLength;
  }, [products, connected, productIds]);

  // Wrap getAvailablePurchases to ensure it always returns an array
  const wrappedGetAvailablePurchases = async (): Promise<any[]> => {
    try {
      const result = await getAvailablePurchases();
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.warn('[IAP Context] getAvailablePurchases failed:', error);
      return [];
    }
  };

  const value: IAPContextType = {
    connected,
    products: products || [],
    fetchProducts,
    requestPurchase,
    finishTransaction,
    getAvailablePurchases: wrappedGetAvailablePurchases,
    productIds,
    isSimulator,
  };

  // Debug: Log when provider renders and provides value
  useEffect(() => {
    console.log('✅ [IAP Context] Provider is rendering and providing context value:', {
      hasValue: !!value,
      connected: value.connected,
      productsCount: value.products?.length || 0,
      productIdsCount: value.productIds?.length || 0,
      isSimulator: value.isSimulator,
      timestamp: new Date().toISOString()
    });
  }, [value.connected, value.products?.length]);

  return (
    <IAPContext.Provider value={value}>
      {children}
    </IAPContext.Provider>
  );
};

